import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core';
import { NavigationError, Router, RouterOutlet } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MsalCustomNavigationClient, MsalService } from '@azure/msal-angular';
import { EMPTY, Observable, Subject, distinctUntilChanged, filter, switchMap, takeUntil, tap } from 'rxjs';
import { AuthService, DataSyncService, DeepLinkService, SignalRHubService, SignInService } from './modules/core/services';
import { Location, AsyncPipe } from '@angular/common';
import { ConfigService } from 'src/config';
import { LoggingService } from './modules/shared/services/logging.service';
import { ContextMenuComponent } from './components/context-menu/context-menu.component';
import { TopBarComponent } from './components/top-bar/top-bar.component';

@Component({
    selector: 'oms-map-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [TopBarComponent, ContextMenuComponent, RouterOutlet, AsyncPipe],
    changeDetection: ChangeDetectionStrategy.Eager
})
export class AppComponent implements OnInit, OnDestroy {
    private _signInService = inject(SignInService);
    private _translate = inject(TranslateService);
    private _router = inject(Router);
    private _msal = inject(MsalService);
    private _location = inject(Location);
    private _loggingService = inject(LoggingService);
    private _authService = inject(AuthService);
    private _configService = inject(ConfigService);

    public userLoaded$: Observable<boolean> = this._signInService.userHasLoaded$;
    public errorMessage: string;
    public isIframe = false;

    public stateHasLoaded$: Observable<boolean>;

    private _signalRHubService = inject(SignalRHubService);
    private _dataSyncService = inject(DataSyncService);
    private _deepLinkService = inject(DeepLinkService);
    private _removeVehicleMessageListener?: () => void;
    private _removeHistoryMessageListener?: () => void;

    private _ngUnsubscribe$ = new Subject<void>();


    constructor() {
        this._loggingService.init(this._configService, this._authService);
        this._translate.setDefaultLang('en_us');
        this._translate.use('en_us');

        this.stateHasLoaded$ = this._signInService.stateHasLoaded$;

        const customNavigationClient = new MsalCustomNavigationClient(this._msal, this._router, this._location);
        this._msal.instance.setNavigationClient(customNavigationClient);
    }

    ngOnInit() {
        this.isIframe = window !== window.parent;

        // Capture launch deep-link early so we can recover once if MSAL redirect strips query params.
        this._deepLinkService.capturePendingFromLocation();

        // Enable OMS.RA.Web to reuse an existing OMS.Map tab by sending vehicle/history updates via postMessage.
        this._removeVehicleMessageListener = this._deepLinkService.registerVehicleMessageListener((vehicle) => {
            this._router.navigate([], { queryParams: { vehicle, view: 'vehicle' }, queryParamsHandling: '' });
        });
        this._removeHistoryMessageListener = this._deepLinkService.registerHistoryMessageListener(({ vehicle, startDateTime, endDateTime }) => {
            this._router.navigate([], {
                queryParams: { vehicle, view: 'history', startDateTime, endDateTime },
                queryParamsHandling: ''
            });
        });
        this._deepLinkService.notifyOpenerReady();

        this._msal.handleRedirectObservable()
          .pipe(takeUntil(this._ngUnsubscribe$))
          .subscribe({
            error: (error) => {
              this._loggingService.logException(error);
            }
          });

        // Redirect stale/malformed URLs (e.g. old bookmarks with wrong outlet paths) to root
        // instead of leaving the user on a broken blank screen.
        this._router.events.pipe(
            filter(e => e instanceof NavigationError),
            takeUntil(this._ngUnsubscribe$)
        ).subscribe((e: NavigationError) => {
            if (e.error?.code === 4002) {
                this._router.navigateByUrl('');
            }
        });

        // load inital state of the application
        this._signInService.load();

        this._signInService.userHasLoaded$.pipe(
            distinctUntilChanged(),
            tap(loaded => {
                if (loaded) this._signalRHubService.init(); 
            }),
            switchMap(loaded => loaded
                ? this._signalRHubService.isConnected$.pipe(
                    distinctUntilChanged(),
                    filter(connected => connected)
                  )
                : EMPTY
            ),
            takeUntil(this._ngUnsubscribe$)
        ).subscribe({
            next: () => {
                this._dataSyncService.init();
            },
            error: error => {
                this._loggingService.logException(error);
            }
        });
    }

    ngOnDestroy() {
        try {
            this._removeVehicleMessageListener?.();
            this._removeHistoryMessageListener?.();
        } catch {
            // ignore
        }
        this._ngUnsubscribe$.next();
        this._ngUnsubscribe$.complete();
    }
}
